import { Alert, Box, Paper } from "@mui/material";
import CustomTable, {
  type CustomTableColumn,
} from "components/common/CustomTable";
import RowActions from "components/common/RowActions";
import DashboardLayout from "components/layouts/DashboardLayout";
import { usePatients } from "hooks/usePatients";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import type { Patient } from "types";
import {
  calculateBirthDateRange,
  formatBirthDate,
  formatPatientName,
} from "utils";
import { formatPhoneWithCountry } from "helpers/validationHelpers";
import { EPath } from "constants/path";

import PatientFilters from "./PatientFilters";
import PatientSearch from "./PatientSearch";
import {
  PatientsPaginationRequestDtoGenderEnum,
  PatientsPaginationRequestDtoSortByEnum,
  PatientsPaginationRequestDtoSortOrderEnum,
} from "libs/cellera-sdk";
import { useCelleraTheme } from "theme/useCelleraTheme";

interface PatientFiltersState {
  search: string;
  gender: PatientsPaginationRequestDtoGenderEnum[];
  ageFrom: number | null;
  ageTo: number | null;
  sortBy: string;
  sortOrder: "asc" | "desc";
  page: number;
  pageSize: number;
}

const DEFAULT_PAGE_SIZE = 10;

const PatientManagement: React.FC = () => {
  const { colors } = useCelleraTheme();
  const navigate = useNavigate();

  const [filters, setFilters] = useState<PatientFiltersState>({
    search: "",
    gender: [],
    ageFrom: null,
    ageTo: null,
    sortBy: "requestedDate",
    sortOrder: "desc",
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300);

    return () => clearTimeout(timer);
  }, [filters.search]);

  const birthDateRange = useMemo(
    () => calculateBirthDateRange(filters.ageFrom, filters.ageTo),
    [filters.ageFrom, filters.ageTo],
  );

  const { patients, totalCount, loading, error, refetch } = usePatients({
    page: filters.page,
    pageSize: filters.pageSize,
    search: debouncedSearch || undefined,
    gender: filters.gender.length > 0 ? filters.gender : undefined,
    birthDateFrom: birthDateRange.birthDateFrom,
    birthDateTo: birthDateRange.birthDateTo,
    sortBy: filters.sortBy as PatientsPaginationRequestDtoSortByEnum,
    sortOrder:
      filters.sortOrder === "asc"
        ? PatientsPaginationRequestDtoSortOrderEnum.Asc
        : PatientsPaginationRequestDtoSortOrderEnum.Desc,
  });

  const handleSearchChange = useCallback((search: string) => {
    setFilters((prev) => ({ ...prev, search, page: 0 }));
  }, []);

  const handleGenderChange = useCallback(
    (genderValues: PatientsPaginationRequestDtoGenderEnum[]) => {
      setFilters((prev) => ({ ...prev, gender: genderValues, page: 0 }));
    },
    [],
  );

  const handleAgeRangeChange = useCallback(
    (ageFrom: number | null, ageTo: number | null) => {
      setFilters((prev) => ({ ...prev, ageFrom, ageTo, page: 0 }));
    },
    [],
  );

  const handleViewPatient = useCallback(
    (patient: Patient) => {
      navigate(EPath.PATIENT_DETAIL.replace(":patientId", patient.patientId));
    },
    [navigate],
  );

  const columns: CustomTableColumn<Patient>[] = useMemo(
    () => [
      {
        id: "email",
        label: "Email",
        sortable: true,
        sortAccessor: (row: Patient) => row.email ?? "",
        getValue: (row: Patient) => row.email ?? "N/A",
      },
      {
        id: "fullName",
        label: "Full Name",
        sortable: true,
        sortAccessor: (row: Patient) => formatPatientName(row),
        getValue: (row: Patient) => formatPatientName(row),
      },
      {
        id: "gender",
        label: "Gender",
        getValue: (row: Patient) => row.gender ?? "N/A",
      },
      {
        id: "birthDate",
        label: "Birthday",
        sortable: true,
        sortAccessor: (row: Patient) =>
          row.birthDate ? new Date(row.birthDate) : null,
        getValue: (row: Patient) =>
          row.birthDate ? formatBirthDate(row.birthDate) : "N/A",
      },
      {
        id: "phoneCountryCode",
        label: "Phone Code",
        getValue: (row: Patient) => row.phoneCountryCode ?? "N/A",
      },
      {
        id: "phone",
        label: "Phone",
        sortable: true,
        sortAccessor: (row: Patient) => row.phone ?? "",
        getValue: (row: Patient) =>
          formatPhoneWithCountry(
            row.phone ?? undefined,
            row.phoneCountryIso2 ?? undefined,
          ) || "N/A",
      },
      {
        id: "actions",
        label: "",
        align: "center",
        getValue: (row: Patient) => (
          <RowActions
            onViewDetails={() => {
              handleViewPatient(row);
            }}
          />
        ),
        width: "5%",
      },
    ],
    [handleViewPatient],
  );

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilters((prev) => ({
        ...prev,
        pageSize: Number.parseInt(event.target.value, 10),
        page: 0,
      }));
    },
    [],
  );

  return (
    <DashboardLayout title="Patient Management">
      <Box className="flex flex-col h-full">
        <Paper
          elevation={0}
          sx={{
            border: `1px solid ${colors.border.light}`,
            borderRadius: 1,
            p: 2,
          }}
        >
          {error && (
            <Alert
              severity="error"
              onClose={() => {
                refetch();
              }}
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}

          <Box className="mb-4">
            <Box className="grid grid-cols-2 gap-4 items-stretch">
              <PatientSearch
                value={filters.search}
                onChange={handleSearchChange}
              />
              <Box className="w-full flex justify-end">
                <PatientFilters
                  gender={filters.gender}
                  ageFrom={filters.ageFrom}
                  ageTo={filters.ageTo}
                  onGenderChange={handleGenderChange}
                  onAgeRangeChange={handleAgeRangeChange}
                />
              </Box>
            </Box>
          </Box>

          <Box className="flex-1">
            <CustomTable<Patient>
              data={patients}
              columns={columns}
              totalCount={totalCount}
              page={filters.page}
              rowsPerPage={filters.pageSize}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              isLoading={loading}
              loadingMessage="Loading patients..."
              tableContainerSx={{
                height: "calc(100vh - 250px)",
              }}
            />
          </Box>
        </Paper>
      </Box>
    </DashboardLayout>
  );
};

export default PatientManagement;
